import React, { useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import { useTranslation } from "react-i18next";

// Long option lists (e.g. ~55 colors, ~35 shoe sizes) start collapsed and get a search box.
const COLLAPSED_COUNT = 14;
const SEARCH_THRESHOLD = 16;

// Swatches offered when adding a custom value to a color-type attribute.
const CUSTOM_SWATCHES = [
  "#000000", "#FFFFFF", "#8E8E8E", "#6F4E37", "#D9C3A0", "#D4AF37",
  "#D32F2F", "#800020", "#FF8C00", "#FFD600", "#708238", "#2E7D32",
  "#008080", "#87CEEB", "#1E63D6", "#000080", "#6A1B9A", "#B57EDC",
  "#F48FB1", "#FF0090",
];

// Option label in the active language (option rows carry value / valueArabic / valueHebrew).
export function getOptionLabel(option, language) {
  if (!option) return "";
  if (language?.startsWith("ar") && option.valueArabic) return option.valueArabic;
  if (language?.startsWith("he") && option.valueHebrew) return option.valueHebrew;
  return option.value;
}

// Attribute name in the active language (category-attribute rows carry attributeName / ...Arabic / ...Hebrew).
export function getAttributeLabel(attribute, language) {
  if (!attribute) return "";
  if (language?.startsWith("ar") && attribute.attributeNameArabic) return attribute.attributeNameArabic;
  if (language?.startsWith("he") && attribute.attributeNameHebrew) return attribute.attributeNameHebrew;
  return attribute.attributeName;
}

function Swatch({ hex, size = 14 }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: hex,
        border: "1px solid",
        borderColor: "divider",
        flexShrink: 0,
      }}
    />
  );
}

// Single-select chip picker for a dropdown attribute (e.g. Size: S/M/L, Color: Black/Navy/...).
// Shows color swatches when options have one, collapses/searches long lists, and lets the
// store add its own value when the catalog doesn't have it (`onAddCustom` resolves to the new option).
export default function AttributeOptionPicker({ attribute, selectedOptionId, onSelect, onAddCustom, disabled }) {
  const { t, i18n } = useTranslation(["boProducts"]);
  const language = i18n.language;
  const options = useMemo(() => attribute.options || [], [attribute.options]);
  const isColorAttribute = options.some((o) => o.colorHex);

  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const [customHex, setCustomHex] = useState(null);
  const [savingCustom, setSavingCustom] = useState(false);
  const [customError, setCustomError] = useState("");

  const trimmedQuery = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!trimmedQuery) return options;
    return options.filter((o) =>
      [o.value, o.valueArabic, o.valueHebrew].some((v) => v && v.toLowerCase().includes(trimmedQuery)),
    );
  }, [options, trimmedQuery]);

  // Collapsed view still keeps the selected option visible even if it's far down the list.
  const visible = useMemo(() => {
    if (expanded || trimmedQuery || filtered.length <= COLLAPSED_COUNT) return filtered;
    const head = filtered.slice(0, COLLAPSED_COUNT);
    const selected = filtered.find((o) => o.optionId === selectedOptionId);
    return selected && !head.includes(selected) ? [...head, selected] : head;
  }, [filtered, expanded, trimmedQuery, selectedOptionId]);

  const hiddenCount = filtered.length - visible.length;
  const example = getOptionLabel(options[options.length > 3 ? 3 : 0], language) || "…";

  const openAdd = () => {
    setCustomValue(query.trim());
    setCustomHex(null);
    setCustomError("");
    setAdding(true);
  };

  const cancelAdd = () => {
    setAdding(false);
    setCustomValue("");
    setCustomHex(null);
    setCustomError("");
  };

  const saveCustom = async () => {
    const value = customValue.trim();
    if (!value || savingCustom) return;
    setSavingCustom(true);
    setCustomError("");
    try {
      const option = await onAddCustom(attribute.attributeId, value, customHex);
      onSelect(option.optionId);
      setQuery("");
      cancelAdd();
    } catch (error) {
      const data = error?.response?.data;
      const message = typeof data === "string" ? data : data?.title || error?.message;
      setCustomError(message || t("boProducts:attributeOptions.addCustomFailed"));
    } finally {
      setSavingCustom(false);
    }
  };

  return (
    <Box>
      {options.length >= SEARCH_THRESHOLD && (
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("boProducts:attributeOptions.search", { name: getAttributeLabel(attribute, language) })}
          disabled={disabled}
          sx={{ mb: 1, maxWidth: 360 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      )}

      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
        {visible.map((option) => {
          const isSelected = selectedOptionId === option.optionId;
          const icon = option.colorHex ? (
            <Swatch hex={option.colorHex} />
          ) : isColorAttribute ? (
            <PaletteOutlinedIcon />
          ) : undefined;
          return (
            <Chip
              key={option.optionId}
              icon={icon}
              label={
                option.isCustom ? (
                  <span>
                    {getOptionLabel(option, language)}
                    <Box component="span" sx={{ ml: 0.75, fontSize: 10, fontWeight: 700, opacity: 0.75 }}>
                      {t("boProducts:attributeOptions.customBadge")}
                    </Box>
                  </span>
                ) : (
                  getOptionLabel(option, language)
                )
              }
              size="small"
              color={isSelected ? "primary" : "default"}
              variant={isSelected ? "filled" : "outlined"}
              onClick={disabled ? undefined : () => onSelect(isSelected ? null : option.optionId)}
              sx={option.colorHex ? { pl: 0.75 } : undefined}
            />
          );
        })}

        {hiddenCount > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={t("boProducts:attributeOptions.showAll", { count: filtered.length })}
            onClick={() => setExpanded(true)}
            sx={{ borderStyle: "dashed", fontWeight: 600 }}
          />
        )}
        {expanded && !trimmedQuery && filtered.length > COLLAPSED_COUNT && (
          <Chip
            size="small"
            variant="outlined"
            label={t("boProducts:attributeOptions.showLess")}
            onClick={() => setExpanded(false)}
            sx={{ borderStyle: "dashed", fontWeight: 600 }}
          />
        )}

        {onAddCustom && !adding && (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            icon={<AddRoundedIcon />}
            label={t("boProducts:attributeOptions.addCustom")}
            onClick={disabled ? undefined : openAdd}
            sx={{ fontWeight: 600 }}
          />
        )}
      </Stack>

      {trimmedQuery && filtered.length === 0 && !adding && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {t("boProducts:attributeOptions.noMatches")}
        </Typography>
      )}

      {adding && (
        <Box
          sx={{
            mt: 1.25,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            maxWidth: 480,
          }}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                saveCustom();
              }
            }}
            placeholder={t("boProducts:attributeOptions.customPlaceholder", { example })}
            disabled={savingCustom}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          {isColorAttribute && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {t("boProducts:attributeOptions.customColorHint")}
              </Typography>
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                {CUSTOM_SWATCHES.map((hex) => {
                  const picked = customHex === hex;
                  return (
                    <Box
                      key={hex}
                      component="button"
                      type="button"
                      aria-label={hex}
                      aria-pressed={picked}
                      onClick={() => setCustomHex(picked ? null : hex)}
                      sx={{
                        p: "3px",
                        border: "2px solid",
                        borderColor: picked ? "primary.main" : "transparent",
                        borderRadius: "50%",
                        bgcolor: "transparent",
                        cursor: "pointer",
                        lineHeight: 0,
                      }}
                    >
                      <Swatch hex={hex} size={22} />
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
          {customError && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
              {customError}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", mt: 1.5 }}>
            <Button size="small" onClick={cancelAdd} disabled={savingCustom}>
              {t("boProducts:attributeOptions.cancel")}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={saveCustom}
              disabled={!customValue.trim() || savingCustom}
              startIcon={savingCustom ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {t("boProducts:attributeOptions.add")}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
